import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  Phone, 
  Mail, 
  MapPin, 
  MessageCircle, 
  CheckCircle, 
  Send, 
  Star, 
  ArrowRight,
  Quote,
  ChevronDown,
  Zap,
  Shield
} from 'lucide-react';
import { ContactFormData } from '../types';
import { trackFormSubmission, trackPhoneClick, trackEmailClick } from '../utils/gtag';
import { brandTheme } from '../styles/BrandTheme';
import HeroSectionComponent from '../components/HeroSection';
import { sendContactEmail } from '../utils/emailService';

const HeroSection = styled.section`
  height: 100vh;
  background: 
    linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%),
    url('/images/resin-surfaces-bound-work10.jpeg') center/cover;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;
  padding: 0 1rem;

  /* iPad Pro and Large Tablets */
  @media (max-width: 1366px) and (min-width: 1025px) {
    height: 100vh;
    padding: 0 2rem;
  }

  /* iPad Air and Medium Tablets */
  @media (max-width: 1024px) and (min-width: 769px) {
    height: 100vh;
    padding: 0 1.5rem;
  }

  /* Landscape Mobile and Small Tablets */
  @media (max-width: 768px) and (min-width: 569px) and (orientation: landscape) {
    height: 100vh;
    padding: 0 1.25rem;
  }

  /* Portrait Mobile - iPhone 12 Pro Max and Large Mobile */
  @media (max-width: 768px) and (min-width: 428px) and (orientation: portrait) {
    height: 100vh;
    padding: 0 1.25rem;
  }

  /* iPhone 12 and Standard Mobile */
  @media (max-width: 427px) and (min-width: 390px) {
    height: 100vh;
    padding: 0 1rem;
  }

  /* iPhone SE and Small Mobile */
  @media (max-width: 389px) and (min-width: 320px) {
    height: 100vh;
    padding: 0 0.75rem;
  }

  /* Landscape orientation adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    height: 100vh;
    padding: 0 1rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 30%, rgba(254, 206, 27, 0.1) 50%, transparent 70%);
    animation: shimmer 3s ease-in-out infinite;
  }

  @keyframes shimmer {
    0%, 100% { transform: translateX(-100%); }
    50% { transform: translateX(100%); }
  }


  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="0.5" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>'),
      url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><defs><pattern id="dots" width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="10" cy="10" r="1" fill="rgba(254,206,27,0.1)"/></pattern></defs><rect width="200" height="200" fill="url(%23dots)"/></svg>');
    opacity: 0.4;
    pointer-events: none;
    animation: float 20s ease-in-out infinite;
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-10px) rotate(1deg); }
  }
`;

const HeroContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  z-index: 2;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 0 0.5rem;
    max-width: 100%;
  }

  @media (max-width: 389px) {
    padding: 0 0.25rem;
  }
`;

const HeroBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(254, 206, 27, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  padding: 0.75rem 1.5rem;
  margin-bottom: 2rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: white;

  svg {
    color: #FECE1B;
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    margin-bottom: 1.5rem;
    font-size: 0.8rem;
  }

  @media (max-width: 389px) {
    padding: 0.4rem 0.75rem;
    margin-bottom: 1rem;
    font-size: 0.75rem;
    gap: 0.25rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    padding: 0.3rem 0.75rem;
    margin-bottom: 0.75rem;
    font-size: 0.7rem;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2rem, 5.5vw, 4.5rem);
  margin-bottom: 1.5rem;
  font-weight: 700;
  line-height: 1.1;
  background: linear-gradient(135deg, #FECE1B 0%, #ffffff 50%, #FECE1B 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);

  /* Mobile adjustments */
  @media (max-width: 389px) {
    font-size: clamp(1.75rem, 5vw, 2.5rem);
    margin-bottom: 1rem;
    line-height: 1.2;
  }

  @media (max-width: 427px) and (min-width: 390px) {
    font-size: clamp(1.875rem, 5.25vw, 3rem);
    margin-bottom: 1.25rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    font-size: clamp(1.5rem, 4vw, 2.5rem);
    margin-bottom: 0.75rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(1rem, 2.5vw, 1.5rem);
  margin-bottom: 2.5rem;
  opacity: 0.95;
  line-height: 1.6;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

  /* Mobile adjustments */
  @media (max-width: 389px) {
    font-size: clamp(0.875rem, 2.25vw, 1.125rem);
    margin-bottom: 1.5rem;
    line-height: 1.5;
    max-width: 100%;
  }

  @media (max-width: 427px) and (min-width: 390px) {
    font-size: clamp(0.9375rem, 2.375vw, 1.25rem);
    margin-bottom: 2rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    font-size: clamp(0.75rem, 2vw, 1rem);
    margin-bottom: 1rem;
    line-height: 1.4;
  }
`;

const CTAButtons = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 3rem;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    margin-bottom: 2rem;
  }

  @media (max-width: 389px) {
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    flex-direction: row;
    gap: 1rem;
    margin-bottom: 1rem;
  }
`;

const StyledButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 2.5rem;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.1rem;
  position: relative;
  overflow: hidden;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 0.875rem 1.75rem;
    font-size: 0.95rem;
    gap: 0.5rem;
  }

  @media (max-width: 427px) and (min-width: 390px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
  }

  @media (max-width: 389px) {
    padding: 0.625rem 1.25rem;
    font-size: 0.85rem;
    gap: 0.375rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
    gap: 0.25rem;
  }

  /* Icon sizing for mobile */
  @media (max-width: 768px) {
    svg {
      width: 16px;
      height: 16px;
    }
  }

  @media (max-width: 389px) {
    svg {
      width: 14px;
      height: 14px;
    }
  }

  @media (orientation: landscape) and (max-height: 500px) {
    svg {
      width: 12px;
      height: 12px;
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &.primary {
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    color: white;
    box-shadow: 0 10px 30px rgba(254, 206, 27, 0.4);
    border: 2px solid transparent;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 15px 40px rgba(254, 206, 27, 0.5);
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(10px);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.5);
      transform: translateY(-2px);
    }
  }
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: #FECE1B;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  padding: 0.75rem 1rem;
  border-radius: 25px;
  border: 1px solid rgba(254, 206, 27, 0.3);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);

  &:hover {
    color: #FECE1B;
    transform: translateX(-50%) translateY(-5px);
    background: rgba(254, 206, 27, 0.1);
    box-shadow: 0 12px 35px rgba(254, 206, 27, 0.2);
  }

  span {
    font-size: 0.85rem;
    font-weight: 600;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }

  svg {
    filter: drop-shadow(0 0 8px rgba(254, 206, 27, 0.5));
    animation: bounce 2s infinite;
  }

  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-10px); }
    60% { transform: translateY(-5px); }
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    bottom: 1.5rem;
    padding: 0.4rem 0.6rem;
    gap: 0.25rem;
    
    span {
      font-size: 0.75rem;
    }
    
    svg {
      width: 16px;
      height: 16px;
    }
  }

  @media (max-width: 427px) and (min-width: 390px) {
    bottom: 1.25rem;
    padding: 0.35rem 0.5rem;
    
    span {
      font-size: 0.7rem;
    }
    
    svg {
      width: 14px;
      height: 14px;
    }
  }

  @media (max-width: 389px) {
    bottom: 1rem;
    padding: 0.3rem 0.4rem;
    gap: 0.2rem;
    
    span {
      font-size: 0.65rem;
    }
    
    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const Section = styled.section`
  padding: 6rem 0;
  position: relative;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  width: 100%;
  overflow-x: hidden;

  @media (max-width: 1024px) {
    padding: 0 1.5rem;
  }

  @media (max-width: 768px) {
    padding: 0 1rem;
  }

  @media (max-width: 480px) {
    padding: 0 0.75rem;
  }

  @media (max-width: 375px) {
    padding: 0 0.5rem;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 1rem;
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 700;
  background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: clamp(1.1rem, 2.5vw, 1.3rem);
  color: #64748b;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin: 3rem 0;

  @media (max-width: 1024px) {
    gap: 3rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  @media (max-width: 480px) {
    gap: 1.5rem;
  }
`;

const ContactInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  h3 {
    color: #FECE1B;
    margin-bottom: 2rem;
    font-size: 1.5rem;
    font-weight: 700;
    grid-column: 1 / -1;
  }
`;

const ContactItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  min-height: 180px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);

    &::before {
      transform: scaleX(1);
    }
  }

  .icon {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #000000;
    flex-shrink: 0;
    box-shadow: 0 10px 30px rgba(254, 206, 27, 0.3);
  }

  .content {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;

    h4 {
      color: #000000;
      margin-bottom: 0.5rem;
      font-weight: 700;
      font-size: 1.1rem;
    }

    p, a {
      color: #64748b;
      text-decoration: none;
      margin-bottom: 0.25rem;
      font-weight: 500;
      font-size: 0.9rem;
    }

    a:hover {
      color: #FECE1B;
    }
  }

  @media (max-width: 1024px) {
    min-height: 160px;
    padding: 1.25rem;
  }

  @media (max-width: 768px) {
    min-height: 140px;
    padding: 1rem;
    
    .icon {
      width: 50px;
      height: 50px;
    }
  }
`;

const FormContainer = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  padding: 2.5rem;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  position: relative;
  overflow: hidden;
  width: 100%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  }

  h3 {
    color: #FECE1B;
    margin-bottom: 2rem;
    font-size: 1.5rem;
    font-weight: 700;
  }

  @media (max-width: 1024px) {
    padding: 2rem;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
  }

  @media (max-width: 375px) {
    padding: 1rem;
  }
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }

  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;

  &.full-width {
    grid-column: 1 / -1;
  }

  label {
    color: #000000;
    font-weight: 600;
    margin-bottom: 0.5rem;
    font-size: 0.95rem;
  }

  input, select, textarea {
    padding: 1rem;
    border: 2px solid #e2e8f0;
    border-radius: 15px;
    font-size: 1rem;
    transition: all 0.3s ease;
    background: #ffffff;
    font-weight: 500;
    width: 100%;

    &:focus {
      outline: none;
      border-color: #FECE1B;
      box-shadow: 0 0 0 3px rgba(254, 206, 27, 0.1);
    }

    &.error {
      border-color: #ef4444;
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
    }

    &::placeholder {
      color: #9ca3af;
      font-weight: 400;
    }
  }

  textarea {
    resize: vertical;
    min-height: 120px;
  }

  .error-message {
    color: #ef4444;
    font-size: 0.875rem;
    margin-top: 0.25rem;
    font-weight: 500;
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  color: #000000;
  padding: 1.25rem 2.5rem;
  border: none;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(254, 206, 27, 0.3);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 20px 40px rgba(254, 206, 27, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessMessage = styled(motion.div)`
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border: 2px solid #10b981;
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  box-shadow: 0 10px 30px rgba(16, 185, 129, 0.1);

  .icon {
    color: #10b981;
    flex-shrink: 0;
  }

  .content {
    h4 {
      color: #065f46;
      margin-bottom: 0.5rem;
      font-weight: 700;
      font-size: 1.2rem;
    }

    p {
      color: #047857;
      margin: 0;
      font-weight: 500;
    }
  }
`;

const ErrorMessage = styled(motion.div)`
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 2px solid #ef4444;
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  box-shadow: 0 10px 30px rgba(239, 68, 68, 0.1);

  .icon {
    color: #ef4444;
    flex-shrink: 0;
  }

  .content {
    h4 {
      color: #991b1b;
      margin-bottom: 0.5rem;
      font-weight: 700;
      font-size: 1.2rem;
    }

    p {
      color: #dc2626;
      margin: 0;
      font-weight: 500;
    }
  }
`;


const MapSection = styled.section`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 6rem 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const MapContainer = styled.div`
  height: 500px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }

  @media (max-width: 768px) {
    height: 400px;
  }
`;

const ServiceAreas = styled.section`
  padding: 6rem 0;
  background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const AreasGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin: 3rem 0;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  @media (max-width: 375px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const AreaCard = styled(motion.div)`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  padding: 2rem;
  border-radius: 20px;
  text-align: center;
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);

    &::before {
      transform: scaleX(1);
    }
  }

  .icon {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
    color: #000000;
    box-shadow: 0 10px 30px rgba(254, 206, 27, 0.3);
  }

  h4 {
    color: #000000;
    margin-bottom: 0.5rem;
    font-weight: 700;
    font-size: 1.1rem;
  }

  p {
    color: #64748b;
    font-size: 0.95rem;
    font-weight: 500;
  }
`;

const FAQSection = styled.section`
  background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
  padding: 6rem 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const FAQGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  max-width: 800px;
  margin: 3rem auto 0;

  @media (max-width: 768px) {
    gap: 1rem;
    margin: 2rem auto 0;
  }
`;

const FAQItem = styled(motion.div)`
  background: #f8fafc;
  border-radius: 15px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;

  &.active {
    background: white;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }

  .question {
    padding: 1.5rem;
    font-weight: 600;
    font-size: 1.1rem;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #000000;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(254, 206, 27, 0.05);
    }

    svg {
      transition: transform 0.3s ease;
      color: #FECE1B;
    }

    &.active svg {
      transform: rotate(180deg);
    }
  }

  .answer {
    padding: 0 1.5rem;
    max-height: 0;
    overflow: hidden;
    transition: all 0.3s ease;
    color: #64748b;
    line-height: 1.6;

    &.active {
      padding: 0 1.5rem 1.5rem;
      max-height: 200px;
    }
  }

  @media (max-width: 768px) {
    .question {
      padding: 1.25rem;
      font-size: 1rem;
    }

    .answer {
      padding: 0 1.25rem;
      
      &.active {
        padding: 0 1.25rem 1.25rem;
      }
    }
  }
`;

const TestimonialsSection = styled.section`
  background: 
    linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, rgba(26, 26, 26, 0.95) 100%),
    url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="dots" width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="10" cy="10" r="1" fill="rgba(254,206,27,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23dots)"/></svg>');
  color: white;
  padding: 6rem 0;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="dots" width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="10" cy="10" r="1" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23dots)"/></svg>');
    opacity: 0.3;
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2.5rem;
  position: relative;
  z-index: 2;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  @media (max-width: 375px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const TestimonialCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  padding: 2.5rem;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;

  .quote-icon {
    position: absolute;
    top: 1.5rem;
    right: 1.5rem;
    opacity: 0.3;
    font-size: 3rem;
  }

  .rating {
    display: flex;
    gap: 0.25rem;
    margin-bottom: 1.5rem;

    svg {
      color: #fbbf24;
      width: 20px;
      height: 20px;
    }
  }

  p {
    font-size: 1.1rem;
    line-height: 1.7;
    margin-bottom: 1.5rem;
    font-style: italic;
  }

  .reviewer {
    font-weight: 700;
    font-size: 1.1rem;
    margin-bottom: 0.25rem;
  }

  .location {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.95rem;
  }

  .project-type {
    background: rgba(255, 255, 255, 0.1);
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.85rem;
    margin-top: 1rem;
    display: inline-block;
  }

  @media (max-width: 768px) {
    padding: 2rem;
    
    .quote-icon {
      font-size: 2.5rem;
    }
    
    p {
      font-size: 1rem;
    }
  }

  @media (max-width: 375px) {
    padding: 1.5rem;
    
    .quote-icon {
      font-size: 2rem;
    }
    
    p {
      font-size: 0.9rem;
    }
    
    .reviewer {
      font-size: 1rem;
    }
    
    .location {
      font-size: 0.85rem;
    }
  }
`;

const ContactPage: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<ContactFormData>();

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    setSubmitError(null);
    
    // Track form submission
    trackFormSubmission('contact_form');
    
    try {
      // Send email using SMTP
      const result = await sendContactEmail(data);
      
      if (result.success) {
        setIsSubmitted(true);
        reset();
        // Scroll to top of page after successful submission
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setSubmitError(result.message);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('Sorry, there was an error sending your message. Please try again or contact us directly at info@resinsurfaceslondon.co.uk');
    } finally {
      setIsSubmitting(false);
    }
  };


  const testimonials = [
    {
      rating: 5,
      review: "Absolutely fantastic work! Our new resin driveway looks amazing and the team was so professional. The installation was completed on time and the quality is outstanding. Highly recommend Resin Surfaces London to anyone looking for premium outdoor surfacing.",
      reviewer: "Sarah Johnson",
      location: "Richmond, Surrey",
      projectType: "Driveway Installation"
    },
    {
      rating: 5,
      review: "The resin pathway around our pool is perfect. It's safe, looks great, and has been completely maintenance-free for 2 years now. The team was knowledgeable and provided excellent service throughout the project.",
      reviewer: "Michael Chen",
      location: "Hampstead, London",
      projectType: "Pool Deck"
    },
    {
      rating: 5,
      review: "Great value for money and excellent customer service. The installation was quick and clean, with no mess left behind. The final result exceeded our expectations and we couldn't be happier.",
      reviewer: "Emma Williams",
      location: "St Albans, Hertfordshire",
      projectType: "Garden Pathway"
    }
  ];

  const faqs = [
    {
      question: 'How much does resin bound surfacing cost in London?',
      answer: 'Resin bound surfacing typically costs £45-65 per square metre in London, depending on project size, site conditions, and chosen aggregate. We provide free, detailed quotes with no hidden costs and transparent pricing breakdowns for all our resin driveway, pathway and pool deck installations.'
    },
    {
      question: 'How long does resin bound surfacing last?',
      answer: 'With proper installation and maintenance, resin bound surfacing can last 15-25 years. We provide a comprehensive 10-year warranty on all our installations, covering both materials and workmanship. Our premium materials and expert installation ensure maximum durability.'
    },
    {
      question: 'Is resin bound surfacing permeable and SUDS compliant?',
      answer: 'Yes! Resin bound surfacing is fully permeable and SUDS compliant, making it perfect for London\'s drainage requirements. Water drains naturally through the surface, preventing flooding and puddles, which is especially important for planning permission and environmental compliance.'
    },
    {
      question: 'Can resin bound be installed over existing surfaces?',
      answer: 'In most cases, yes. We can install resin bound surfacing over concrete, tarmac, and other solid surfaces. The existing surface needs to be stable and properly prepared. We\'ll assess this during our free survey and advise accordingly, ensuring the best possible installation.'
    },
    {
      question: 'What areas do you cover for resin bound installation?',
      answer: 'We serve all London boroughs, Surrey, Hertfordshire, Buckinghamshire, Kent, Essex, Berkshire, and Oxfordshire. Our service area includes Richmond, Kingston, Croydon, St Albans, Watford, Hemel Hempstead, Canterbury, Maidstone, Chelmsford, Colchester, Aylesbury, High Wycombe, Reading, Windsor, and many more locations. Contact us to confirm coverage in your specific area.'
    },
    {
      question: 'How long does resin bound installation take?',
      answer: 'Most residential resin bound projects take 1-3 days to complete, depending on size and complexity. Commercial projects may take longer. We provide detailed timelines during our free consultation and keep you updated throughout the installation process.'
    },
    {
      question: 'What is the difference between resin bound and resin bonded?',
      answer: 'Resin bound surfacing has aggregates mixed throughout the resin, creating a smooth, permeable surface. Resin bonded has aggregates scattered on top of wet resin, creating a rougher, less permeable surface. Resin bound is more durable, permeable, and suitable for driveways and pathways.'
    },
    {
      question: 'Do you provide free quotes and surveys?',
      answer: 'Yes! We provide completely free, no-obligation quotes and site surveys for all resin bound projects. Our expert team will visit your property, assess the site conditions, discuss your requirements, and provide a detailed quote with transparent pricing and timelines.'
    },
    {
      question: 'What maintenance does resin bound surfacing require?',
      answer: 'Resin bound surfacing requires minimal maintenance. Regular sweeping and occasional pressure washing is usually sufficient. We provide a comprehensive maintenance guide with your installation and offer ongoing support to ensure your surface looks great for years to come.'
    },
    {
      question: 'Are you fully insured and qualified?',
      answer: 'Yes, we are fully insured and qualified resin bound specialists with over 10 years of experience. We have comprehensive public liability insurance, professional indemnity insurance, and all necessary qualifications. We\'re also members of relevant trade associations and provide full warranties on all work.'
    }
  ];

  const serviceAreas = [
    { name: 'London', description: 'All London boroughs', icon: <MapPin size={24} /> },
    { name: 'Surrey', description: 'Richmond, Kingston, Croydon', icon: <MapPin size={24} /> },
    { name: 'Hertfordshire', description: 'St Albans, Watford, Hemel', icon: <MapPin size={24} /> },
    { name: 'Buckinghamshire', description: 'Slough, High Wycombe', icon: <MapPin size={24} /> },
    { name: 'Kent', description: 'Dartford, Sevenoaks', icon: <MapPin size={24} /> },
    { name: 'Essex', description: 'Epping, Harlow', icon: <MapPin size={24} /> },
    { name: 'Berkshire', description: 'Windsor, Maidenhead', icon: <MapPin size={24} /> },
    { name: 'Oxfordshire', description: 'Oxford, Bicester', icon: <MapPin size={24} /> }
  ];

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  if (isSubmitted) {
    return (
      <>
        <Helmet>
          <title>Thank You | Resin Surfaces London | Quote Request Received | We'll Contact You Within 24 Hours</title>
          <meta name="description" content="Thank you for your resin surfacing quote request! Resin Surfaces London will contact you within 24 hours to discuss your project. 500+ projects, 10+ year warranty, 5★ reviews. Expert resin bound installation across London, Surrey & Hertfordshire." />
          <meta name="keywords" content="resin surfacing quote received, resin bound consultation London, resin driveway quote London, resin pathway quote London, resin pool deck quote London, thank you resin surfaces London" />
          <link rel="canonical" href="https://www.resinsurfaceslondon.co.uk/contact/" />
        </Helmet>

        <HeroSection>
          <HeroContent>
            <HeroTitle
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Thank You for Your Enquiry
            </HeroTitle>
            <HeroSubtitle
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              We'll be in touch within 24 hours to discuss your project
            </HeroSubtitle>
          </HeroContent>
        </HeroSection>

        <Section>
          <Container>
            <SuccessMessage
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <CheckCircle size={48} className="icon" />
              <div className="content">
                <h4>Enquiry Received Successfully!</h4>
                <p>Thank you for your interest in Resin Surfaces London. We'll contact you within 24 hours to discuss your project and arrange a free survey.</p>
              </div>
            </SuccessMessage>
          </Container>
        </Section>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Contact Resin Surfaces London | Resin Driveways, Patios & Paving Quotes</title>
        <meta name="description" content="Contact Resin Surfaces London for resin bound driveways, patios & paving. Free quotes, 500+ installs, ⭐⭐⭐⭐⭐ reviews and 10+ year warranty. Call 07535 935 779, WhatsApp or email info@resinsurfaceslondon.co.uk." />
        <meta name="keywords" content="contact resin surfaces London, resin driveway quote London, resin patio quote London, resin paving quote London, resin courtyard London, resin bound installers London, resin driveway contractors London, resin patio specialists London, permeable paving London, SUDS compliant driveways London, resin patios Surrey, resin paving Hertfordshire, resin driveway quotes Surrey, resin patio quotes Hertfordshire, resin paving near me London" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Resin Surfaces London" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="canonical" href="https://www.resinsurfaceslondon.co.uk/contact/" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.resinsurfaceslondon.co.uk/contact/" />
        <meta property="og:title" content="Contact Resin Surfaces London | Resin Driveways, Patios & Paving" />
        <meta property="og:description" content="Reach out for free quotes on resin driveways, patios & paving. 500+ installs, ⭐⭐⭐⭐⭐ reviews & 10+ year warranty. Call 07535 935 779!" />
        <meta property="og:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work10.jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Resin Surfaces London" />
        <meta property="og:locale" content="en_GB" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.resinsurfaceslondon.co.uk/contact/" />
        <meta name="twitter:title" content="Contact Resin Surfaces London | Resin Driveways, Patios & Paving" />
        <meta name="twitter:description" content="Get free quotes for resin driveways, patios & paving. 500+ installs, ⭐⭐⭐⭐⭐ reviews & 10+ year warranty." />
        <meta name="twitter:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work10.jpeg" />
        <meta name="twitter:site" content="@ResinSurfacesLdn" />
        <meta name="twitter:creator" content="@ResinSurfacesLdn" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="geo.region" content="GB-LND" />
        <meta name="geo.placename" content="London" />
        <meta name="geo.position" content="51.5074;-0.1278" />
        <meta name="ICBM" content="51.5074, -0.1278" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="1 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        <meta name="target" content="all" />
        <meta name="HandheldFriendly" content="True" />
        <meta name="MobileOptimized" content="320" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "@id": "https://www.resinsurfaceslondon.co.uk/contact/#contactpage",
            "name": "Contact Resin Surfaces London - #1 Resin Bound Specialists",
            "description": "Contact page for #1 resin bound surfacing specialists in London. Get free quotes for resin driveways, pathways and pool decks. 500+ projects, 10+ year warranty, 5★ reviews.",
            "url": "https://www.resinsurfaceslondon.co.uk/contact/",
            "mainEntity": {
              "@type": "LocalBusiness",
              "@id": "https://www.resinsurfaceslondon.co.uk/#organization",
              "name": "ZAN RESIN SURFACES LTD",
              "alternateName": "Resin Surfaces London",
              "description": "🏆 #1 Premium resin bound surfacing specialists for driveways, pathways and pool decks across London, Surrey, Hertfordshire, Kent, Essex, Buckinghamshire and Berkshire. 500+ projects, 10+ year warranty, 5★ reviews.",
              "telephone": "+44 7535 935779",
              "email": "info@resinsurfaceslondon.co.uk",
              "vatID": "16698534",
              "logo": "https://www.resinsurfaceslondon.co.uk/logo.png",
              "image": "https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work10.jpeg",
              "foundingDate": "2013",
              "numberOfEmployees": "10-20",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Broadfield Broadhurst Gardens",
                "addressLocality": "London",
                "addressRegion": "London",
                "postalCode": "NW6 3BN",
                "addressCountry": "GB"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": 51.5074,
                "longitude": -0.1278
              },
              "areaServed": [
                {"@type": "City", "name": "London"},
                {"@type": "City", "name": "Surrey"},
                {"@type": "City", "name": "Hertfordshire"},
                {"@type": "City", "name": "Buckinghamshire"},
                {"@type": "City", "name": "Kent"},
                {"@type": "City", "name": "Essex"},
                {"@type": "City", "name": "Berkshire"},
                {"@type": "City", "name": "Oxfordshire"}
              ],
              "serviceType": [
                "Resin Bound Surfacing", 
                "Driveway Installation", 
                "Pathway Installation", 
                "Pool Deck Installation",
                "Permeable Surfacing",
                "SUDS Compliant Surfacing",
                "Anti-slip Surfacing",
                "Commercial Resin Surfacing",
                "Residential Resin Driveways"
              ],
              "priceRange": "££",
              "paymentAccepted": ["Cash", "Card", "Bank Transfer"],
              "currenciesAccepted": "GBP",
              "openingHours": "Mo-Fr 08:00-18:00,Sa 09:00-16:00",
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "5.0",
                "reviewCount": "127",
                "bestRating": "5",
                "worstRating": "1"
              },
              "review": [
                {
                  "@type": "Review",
                  "reviewRating": {
                    "@type": "Rating",
                    "ratingValue": "5",
                    "bestRating": "5"
                  },
                  "author": {
                    "@type": "Person",
                    "name": "Sarah Johnson"
                  },
                  "reviewBody": "Absolutely fantastic work! Our new resin driveway looks amazing and the team was so professional. Highly recommend Resin Surfaces London."
                }
              ],
              "hasOfferCatalog": {
                "@type": "OfferCatalog",
                "name": "Resin Bound Services",
                "itemListElement": [
                  {
                    "@type": "Offer",
                    "itemOffered": {
                      "@type": "Service",
                      "name": "Resin Driveways London",
                      "description": "Premium resin bound driveway installation with 10+ year warranty"
                    },
                    "price": "45-65",
                    "priceCurrency": "GBP",
                    "priceSpecification": {
                      "@type": "PriceSpecification",
                      "price": "45-65",
                      "priceCurrency": "GBP",
                      "unitCode": "MTK"
                    }
                  },
                  {
                    "@type": "Offer",
                    "itemOffered": {
                      "@type": "Service",
                      "name": "Resin Patios & Paving London",
                      "description": "Resin bound patios, courtyards and paving upgrades for outdoor living"
                    }
                  },
                  {
                    "@type": "Offer",
                    "itemOffered": {
                      "@type": "Service",
                      "name": "Resin Pool Decks London",
                      "description": "Safe and stylish resin bound pool deck installation with anti-slip finish"
                    }
                  }
                ]
              },
              "sameAs": [
                "https://www.facebook.com/resinsurfaceslondon",
                "https://www.instagram.com/resinsurfaceslondon",
                "https://www.linkedin.com/company/resin-surfaces-london"
              ]
            }
          })}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": "https://www.resinsurfaceslondon.co.uk/contact/#webpage",
            "url": "https://www.resinsurfaceslondon.co.uk/contact/",
            "name": "Contact Resin Surfaces London - #1 Resin Bound Specialists",
            "description": "Contact page for #1 resin bound driveway, patio and paving specialists in London. Get free quotes for seamless outdoor surfaces.",
            "isPartOf": {
              "@id": "https://www.resinsurfaceslondon.co.uk/#website"
            },
            "about": {
              "@id": "https://www.resinsurfaceslondon.co.uk/#organization"
            },
            "publisher": {
              "@id": "https://www.resinsurfaceslondon.co.uk/#organization"
            },
            "potentialAction": [
              {
                "@type": "SearchAction",
                "target": {
                  "@type": "EntryPoint",
                  "urlTemplate": "https://www.resinsurfaceslondon.co.uk/search?q={search_term_string}"
                },
                "query-input": "required name=search_term_string"
              }
            ]
          })}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How much does resin bound surfacing cost?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Resin bound surfacing typically costs £45-65 per square metre, depending on project size, site conditions, and chosen aggregate. We provide free, detailed quotes with no hidden costs."
                }
              },
              {
                "@type": "Question",
                "name": "How long does resin bound surfacing last?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "With proper installation and maintenance, resin bound surfacing can last 15-25 years. We provide a comprehensive 10-year warranty on all our installations."
                }
              },
              {
                "@type": "Question",
                "name": "Is resin bound surfacing permeable?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes! Resin bound surfacing is fully permeable and SUDS compliant. Water drains naturally through the surface, preventing flooding and puddles."
                }
              },
              {
                "@type": "Question",
                "name": "What areas do you cover?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We serve all London boroughs, Surrey, Hertfordshire, Buckinghamshire, Kent, Essex, Berkshire, and Oxfordshire. Contact us to confirm coverage in your specific area."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <HeroSectionComponent
        title="Get Your Free Resin Bound Quote Today"
        subtitle="Contact London's #1 resin bound specialists for expert advice and a no-obligation quote for your resin driveway, pathway or pool deck project. 500+ projects completed, 10+ year warranty, 5★ reviews."
        badge="500+ Projects Completed • 10+ Year Warranty"
        backgroundImage="/images/resin-surfaces-bound-work10.jpeg"
        primaryButtonText="Get Free Quote"
        primaryButtonLink="/contact/"
        secondaryButtonText="Call Now"
        secondaryButtonLink="tel:+447535935779"
      />


      <Section>
        <Container>
          <SectionTitle>Get In Touch with London's #1 Resin Bound Specialists</SectionTitle>
          <SectionSubtitle>
            Contact Resin Surfaces London for expert resin driveway, pathway and pool deck installation. Multiple ways to get your free quote for premium resin bound surfacing across London, Surrey & Hertfordshire.
          </SectionSubtitle>
          <ContactGrid>
            <ContactInfo>
              <h3>Contact Information</h3>
              
              <ContactItem>
                <div className="icon">
                  <Phone size={24} />
                </div>
                <div className="content">
                  <h4>Phone</h4>
                  <a href="tel:+447535935779" onClick={() => trackPhoneClick('+447535935779')}>07535 935 779</a>
                  <p>Mon-Fri: 8am-6pm, Sat: 9am-4pm</p>
                </div>
              </ContactItem>

              <ContactItem>
                <div className="icon">
                  <Mail size={24} />
                </div>
                <div className="content">
                  <h4>Email</h4>
                  <a href="mailto:info@resinsurfaceslondon.co.uk" onClick={() => trackEmailClick('info@resinsurfaceslondon.co.uk')}>info@resinsurfaceslondon.co.uk</a>
                  <p>We respond within 24 hours</p>
                </div>
              </ContactItem>

              <ContactItem>
                <div className="icon">
                  <MessageCircle size={24} />
                </div>
                <div className="content">
                  <h4>WhatsApp</h4>
                  <a href="https://wa.me/447535935779">Chat with us instantly</a>
                  <p>Available 9am-5pm</p>
                </div>
              </ContactItem>

              <ContactItem>
                <div className="icon">
                  <MapPin size={24} />
                </div>
                <div className="content">
                  <h4>Service Areas</h4>
                  <p>London, Surrey, Hertfordshire, Kent, Essex, Buckinghamshire, Berkshire</p>
                  <p>Free surveys across our coverage area</p>
                </div>
              </ContactItem>
            </ContactInfo>

            <FormContainer>
              <h3>Request a Free Quote</h3>
              {submitError && (
                <ErrorMessage
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="icon">⚠️</div>
                  <div className="content">
                    <h4>Error Sending Message</h4>
                    <p>{submitError}</p>
                  </div>
                </ErrorMessage>
              )}
              <Form onSubmit={handleSubmit(onSubmit)}>
                <FormGroup>
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    {...register('name', { required: 'Name is required' })}
                    className={errors.name ? 'error' : ''}
                  />
                  {errors.name && <span className="error-message">{errors.name.message}</span>}
                </FormGroup>

                <FormGroup>
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    className={errors.email ? 'error' : ''}
                  />
                  {errors.email && <span className="error-message">{errors.email.message}</span>}
                </FormGroup>

                <FormGroup>
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    {...register('phone', { required: 'Phone number is required' })}
                    className={errors.phone ? 'error' : ''}
                  />
                  {errors.phone && <span className="error-message">{errors.phone.message}</span>}
                </FormGroup>

                <FormGroup>
                  <label htmlFor="postcode">Postcode *</label>
                  <input
                    type="text"
                    id="postcode"
                    {...register('postcode', { required: 'Postcode is required' })}
                    className={errors.postcode ? 'error' : ''}
                    placeholder="e.g. SW1A 1AA"
                  />
                  {errors.postcode && <span className="error-message">{errors.postcode.message}</span>}
                </FormGroup>

                <FormGroup className="full-width">
                  <label htmlFor="serviceType">Service Required *</label>
                  <select
                    id="serviceType"
                    {...register('serviceType', { required: 'Please select a service' })}
                    className={errors.serviceType ? 'error' : ''}
                  >
                    <option value="">Select a service</option>
                    <option value="driveway">Resin Driveway</option>
                    <option value="pathway">Resin Pathway</option>
                    <option value="pool-deck">Pool Deck</option>
                    <option value="patio">Patio</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.serviceType && <span className="error-message">{errors.serviceType.message}</span>}
                </FormGroup>

                <FormGroup className="full-width">
                  <label htmlFor="message">Project Details</label>
                  <textarea
                    id="message"
                    {...register('message')}
                    placeholder="Tell us about your project - size, current surface, timeline, etc."
                  />
                </FormGroup>

                <FormGroup className="full-width">
                  <SubmitButton type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <div className="spinner"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={20} />
                        Send Quote Request
                      </>
                    )}
                  </SubmitButton>
                </FormGroup>
              </Form>
            </FormContainer>
          </ContactGrid>
        </Container>
      </Section>

      <MapSection>
        <Container>
          <SectionTitle>Our Coverage Area</SectionTitle>
          <MapContainer>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d317715.7119263355!2d-0.3817834!3d51.5287352!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a00baf21de75%3A0x52963a5addd52a99!2sLondon%2C%20UK!5e0!3m2!1sen!2suk!4v1634567890123!5m2!1sen!2suk"
              loading="lazy"
              title="Resin Surfaces London Coverage Area"
            ></iframe>
          </MapContainer>
        </Container>
      </MapSection>

      <FAQSection>
        <Container>
          <SectionTitle>Frequently Asked Questions About Resin Bound Surfacing</SectionTitle>
          <SectionSubtitle>
            Get answers to the most common questions about resin bound surfacing, installation, costs, and maintenance. Our expert team is here to help with all your resin driveway, pathway and pool deck queries.
          </SectionSubtitle>
          <FAQGrid>
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                className={openFAQ === index ? 'active' : ''}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div 
                  className={`question ${openFAQ === index ? 'active' : ''}`}
                  onClick={() => toggleFAQ(index)}
                >
                  {faq.question}
                  <ChevronDown size={20} />
                </div>
                <div className={`answer ${openFAQ === index ? 'active' : ''}`}>
                  {faq.answer}
                </div>
              </FAQItem>
            ))}
          </FAQGrid>
        </Container>
      </FAQSection>

      <TestimonialsSection>
        <Container>
          <SectionTitle style={{ color: 'white' }}>What Our Customers Say About Our Resin Bound Services</SectionTitle>
          <SectionSubtitle style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Real reviews from satisfied customers who have transformed their outdoor spaces with our premium resin driveway, pathway and pool deck installations across London, Surrey, Hertfordshire, Kent, Essex, Buckinghamshire and Berkshire
          </SectionSubtitle>
          <TestimonialsGrid>
            {testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Quote className="quote-icon" />
                <div className="rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} fill="currentColor" />
                  ))}
                </div>
                <p>"{testimonial.review}"</p>
                <div className="reviewer">{testimonial.reviewer}</div>
                <div className="location">{testimonial.location}</div>
                <div className="project-type">{testimonial.projectType}</div>
              </TestimonialCard>
            ))}
          </TestimonialsGrid>
        </Container>
      </TestimonialsSection>

      <ServiceAreas>
        <Container>
          <SectionTitle>Areas We Cover for Resin Bound Surfacing</SectionTitle>
          <SectionSubtitle>
            Professional resin driveway, pathway and pool deck installation services across London, Surrey, Hertfordshire, Buckinghamshire, Kent, Essex, Berkshire and Oxfordshire. Contact us for your free quote today.
          </SectionSubtitle>
          <AreasGrid>
            {serviceAreas.map((area, index) => (
              <AreaCard
                key={area.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="icon">{area.icon}</div>
                <h4>{area.name}</h4>
                <p>{area.description}</p>
              </AreaCard>
            ))}
          </AreasGrid>
        </Container>
      </ServiceAreas>
    </>
  );
};

export default ContactPage;
